import { Component, Story, Knobs, Accents } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  value: {
    default: 0.4,
    editor: 'number',
  },
  label: {
    default: true,
    editor: 'checkbox',
  },
  min: {
    default: 0,
    editor: 'number',
  },
  max: {
    default: 1,
    editor: 'number',
  },
  step: {
    default: 0.05,
    editor: 'number',
  },
  dataType: {
    default: 'decimal',
    editor: 'dropdown',
    editorOptions: ['integer', 'decimal', 'currency'],
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  accent: {
    default: 'midnightblue',
    editor: 'dropdown',
    editorOptions: Accents,
  },
  formattingOptions: {
    default:
      '{"style": "percent", "maximumFractionDigits":0, "minimumFractionDigits":0, "minimumIntegerDigits":1, "useGrouping":false}',
    editor: 'multilinetextbox',
  },
};

const PlaygroundKnobsV3: Knobs = {
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
  value: {
    default: 0.4,
    editor: 'number',
  },
  suppressLabel: {
    default: false,
    editor: 'checkbox',
  },
  min: {
    default: 0,
    editor: 'number',
  },
  max: {
    default: 1,
    editor: 'number',
  },
  step: {
    default: 0.05,
    editor: 'number',
  },
  dataType: {
    default: 'decimal',
    editor: 'dropdown',
    editorOptions: ['integer', 'decimal', 'currency'],
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  accent: {
    default: 'midnightblue',
    editor: 'dropdown',
    editorOptions: Accents,
  },
  formattingOptions: {
    default:
      '{"style": "percent", "maximumFractionDigits":0, "minimumFractionDigits":0, "minimumIntegerDigits":1, "useGrouping":false}',
    editor: 'multilinetextbox',
  },
  labelStyle: {
    default: '',
    editor: 'multilinetextbox',
  },
  name: {
    default: 'Slider1',
    editor: 'multilinetextbox',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-slider where you can change all the attributes',
  coreComponent: {
    html: `<tag-slider{{...knobs}}>
</tag-slider>`,
    knobs: PlaygroundKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-slider{{...knobs}}>
</tag3-slider>
`,
    knobs: PlaygroundKnobsV3,
  },
  reactWrapper: { jsx: `<TagSlider{{...knobs}} />`, knobs: PlaygroundKnobs },
  reactWrapper_v3: {
    jsx: `<Tag3Slider{{...knobs}}> </Tag3Slider>`,
    knobs: {
      ...PlaygroundKnobsV3,
      labelStyle: { ...PlaygroundKnobsV3.labelStyle, qualifierStart: '{', qualifierEnd: '}' },
    },
  },
};

export const TagSliderDocs: Component = {
  type: 'component',
  name: 'slider',
  description: 'Renders a slider',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'id',
      description: 'The id for the input',
      type: 'string',
    },
    {
      name: 'name',
      description: 'The name for the input',
      type: 'string',
    },
    {
      name: 'min',
      default: '0',
      description: 'The minimum value',
      type: 'number',
    },
    {
      name: 'max',
      default: '100',
      description: 'The maximum value',
      type: 'number',
    },
    {
      name: 'step',
      default: '1',
      description: 'The increment value when using the keyboard arrow buttons',
      type: 'number',
    },
    {
      name: 'value',
      default: '0',
      description: 'The value',
      type: 'number',
    },
    {
      name: 'label',
      description: 'The visibility of the value. DEPRECATED in V3',
      type: 'boolean',
    },
    {
      name: 'supressLabel',
      description: 'V3 only: Supress the visibility of the value.',
      type: 'boolean',
    },
    {
      name: 'accent',
      default: 'dolphinblue',
      description: 'The colour of the slider',
      type: 'Accent',
    },
    {
      name: 'device',
      description: 'Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
    {
      name: 'colourScheme',
      description:
        'Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'disabled',
      description: 'Whether the slider is disabled',
      type: 'boolean',
    },
    {
      name: 'dataType',
      default: 'integer',
      description: 'The type of the field that is used for value formatting',
      type:
        'string | integer | decimal | currency | date | datetime | boolean | image | email | phonenumber',
    },
    {
      name: 'formattingOptions',
      description:
        'Additional formatting options for numbers. This maps directly to the options parameter in Intl.NumberFormat see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat',
      type: 'string',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'valueChange',
      description: 'V1 only: Raised when the value changes',
      parameters: [{ name: 'value', description: 'The new value', type: 'number' }],
    },
    {
      name: 'sliderValueChange',
      description: 'V3 only: Raised when the value changes',
      parameters: [{ name: 'value', description: 'The new value', type: 'number' }],
    },
  ],
  methods: [
    {
      name: 'focusSlider',
      description: 'Sets the focus to the slider',
      parameters: [],
      returnType: 'void',
    },
  ],
  stories: [PlaygroundStory],
};
